import { FACEBOOK_PHOTOS, FACEBOOK_VIDEOS, FACEBOOK_FEED, INSTAGRAM_API, LINKEDIN_API, LINKEDIN_VIDEO_API } from "../Config/index.js";


export const postToFacebook = async (payload) => {
  try {
    console.log('Posting to Facebook with payload:', payload);

    // Determine the correct webhook endpoint based on content type (existing logic)
    let webhookUrl;
    switch (payload.contentType) {
      case 'Image':
        webhookUrl = `${FACEBOOK_PHOTOS}`;
        break;
      case 'Video':
        webhookUrl = `${FACEBOOK_VIDEOS}`;
        break;
      case 'Content Only':
      default:
        webhookUrl = `${FACEBOOK_FEED}`;
        break;
    }

    console.log(`Using Facebook webhook endpoint: ${webhookUrl} for content type: ${payload.contentType}`);

    // EXISTING FACEBOOK LOGIC: For Image content type, process each account individually
    if (payload.contentType === 'Image') {
      const results = [];
      const errors = [];
      let successCount = 0;
      let failureCount = 0;

      // Loop through each account and make individual API calls
      for (let i = 0; i < payload.accounts.length; i++) {
        const account = payload.accounts[i];
        
        try {
          console.log(`Processing Facebook Image post for account ${i + 1}/${payload.accounts.length}:`, {
            accountId: account.accountId,
            accountName: account.accountName,
            provider: account.provider
          });

          const formData = new FormData();
          
          // Add all non-file data as form fields
          formData.append('platforms', JSON.stringify(payload.platforms));
          formData.append('otherPlatform', payload.otherPlatform || '');
          formData.append('contentType', payload.contentType);
          formData.append('title', payload.title);
          formData.append('hashtags', payload.hashtags || '');
          formData.append('postContent', payload.postContent);
          formData.append('accounts', JSON.stringify([account])); // Pass only one account per API call
          formData.append('timestamp', payload.timestamp);
          formData.append('clientId', payload.clientId);
          formData.append('accountIndex', (i + 1).toString());
          formData.append('totalAccounts', payload.accounts.length.toString());

          // Handle multiple media files with consistent "data" field naming
          if (payload.mediaFiles && Array.isArray(payload.mediaFiles) && payload.mediaFiles.length > 0) {
            formData.append('mediaFileCount', payload.mediaFiles.length.toString());
            
            payload.mediaFiles.forEach((mediaFile, index) => {
              try {
                const byteCharacters = atob(mediaFile.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let j = 0; j < byteCharacters.length; j++) {
                  byteNumbers[j] = byteCharacters.charCodeAt(j);
                }
                const byteArray = new Uint8Array(byteNumbers);
                
                const file = new File([byteArray], mediaFile.name, { 
                  type: mediaFile.type 
                });
                
                // Use "data" as the field name for all files
                formData.append('data', file);
                formData.append(`fileName_${index}`, mediaFile.name);
                formData.append(`fileType_${index}`, mediaFile.type);
                formData.append(`fileSize_${index}`, mediaFile.size.toString());
                
                console.log(`Added file ${index + 1} as "data" for Facebook account ${account.accountId}:`, {
                  name: mediaFile.name,
                  type: mediaFile.type,
                  size: `${(mediaFile.size / 1024 / 1024).toFixed(2)} MB`
                });
              } catch (fileError) {
                console.error(`Error processing file ${index} for Facebook account ${account.accountId}:`, fileError);
                throw new Error(`Failed to process file: ${mediaFile.name}`);
              }
            });
          }
          // FALLBACK: Handle old single file structure
          else if (payload.mediaFile && payload.mediaFile.data) {
            formData.append('mediaFileCount', '1');
            
            const byteCharacters = atob(payload.mediaFile.data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let j = 0; j < byteCharacters.length; j++) {
              byteNumbers[j] = byteCharacters.charCodeAt(j);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const file = new File([byteArray], payload.mediaFile.name, { 
              type: payload.mediaFile.type 
            });
            
            formData.append('data', file);
            formData.append('fileName_0', payload.mediaFile.name);
            formData.append('fileType_0', payload.mediaFile.type);
            formData.append('fileSize_0', payload.mediaFile.size.toString());
            
            console.log(`Added single file as "data" for Facebook account ${account.accountId}:`, {
              name: payload.mediaFile.name,
              type: payload.mediaFile.type,
              size: `${(payload.mediaFile.size / 1024 / 1024).toFixed(2)} MB`
            });
          }

          console.log(`Sending FormData for Facebook account ${account.accountId} with`, formData.get('mediaFileCount'), 'files');

          const response = await fetch(webhookUrl, {
            method: 'POST',
            body: formData
          });

          // Handle response for this account
          if (!response.ok) {
            const errorText = await response.text();
            console.error(`Facebook API error for account ${account.accountId}:`, response.status, errorText);
            
            let errorMessage = `Failed to post image to Facebook ${account.accountName} (${account.provider})`;
            
            switch (response.status) {
              case 400:
                errorMessage += ': Invalid post data';
                break;
              case 401:
                errorMessage += ': Authentication failed';
                break;
              case 403:
                errorMessage += ': Permission denied';
                break;
              case 413:
                errorMessage += ': File size too large';
                break;
              case 415:
                errorMessage += ': Unsupported media format';
                break;
              case 429:
                errorMessage += ': Rate limit exceeded';
                break;
              case 500:
                errorMessage += ': Server error';
                break;
              default:
                errorMessage += `: Server error (${response.status})`;
            }
            
            errors.push({
              account: account,
              error: errorMessage,
              status: response.status
            });
            failureCount++;
            
            // Continue to next account instead of throwing
            continue;
          }

          const result = await response.json();
          console.log(`Facebook Image post success for account ${account.accountId}:`, result);
          
          results.push({
            account: account,
            result: result,
            success: true
          });
          successCount++;

          // Add delay between requests to avoid rate limiting
          if (i < payload.accounts.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
          }

        } catch (accountError) {
          console.error(`Error processing Facebook image post for account ${account.accountId}:`, accountError);
          
          errors.push({
            account: account,
            error: accountError.message || 'Unknown error occurred',
            status: 'network_error'
          });
          failureCount++;
        }
      }

      // Return comprehensive results for Facebook Image posts
      console.log(`Facebook Image API calls completed: ${successCount} successful, ${failureCount} failed`);

      // Create summary message
      let summaryMessage;
      if (successCount === payload.accounts.length) {
        const fileCount = payload.mediaFiles?.length || (payload.mediaFile ? 1 : 0);
        summaryMessage = `Facebook image post with ${fileCount} file${fileCount > 1 ? 's' : ''} successfully sent to all ${successCount} account${successCount > 1 ? 's' : ''}!`;
      } else if (successCount > 0) {
        summaryMessage = `Facebook image post sent to ${successCount} of ${payload.accounts.length} accounts. ${failureCount} failed.`;
      } else {
        summaryMessage = `Failed to send Facebook image post to all accounts. Please check your account connections.`;
      }

      return {
        success: successCount > 0,
        data: {
          results: results,
          errors: errors,
          summary: {
            total: payload.accounts.length,
            successful: successCount,
            failed: failureCount,
            successRate: Math.round((successCount / payload.accounts.length) * 100)
          }
        },
        message: summaryMessage,
        endpoint: webhookUrl,
        isPartialSuccess: successCount > 0 && failureCount > 0
      };
    }
    // EXISTING FACEBOOK LOGIC: Keep existing logic for Video and Content Only cases
    else if (payload.contentType === 'Video') {
      const formData = new FormData();
      
      formData.append('platforms', JSON.stringify(payload.platforms));
      formData.append('otherPlatform', payload.otherPlatform || '');
      formData.append('contentType', payload.contentType);
      formData.append('title', payload.title);
      formData.append('hashtags', payload.hashtags || '');
      formData.append('postContent', payload.postContent);
      formData.append('accounts', JSON.stringify(payload.accounts));
      formData.append('timestamp', payload.timestamp);
      formData.append('clientId', payload.clientId);

      if (payload.mediaFiles && Array.isArray(payload.mediaFiles) && payload.mediaFiles.length > 0) {
        formData.append('mediaFileCount', payload.mediaFiles.length.toString());
        
        payload.mediaFiles.forEach((mediaFile, index) => {
          try {
            const byteCharacters = atob(mediaFile.data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const file = new File([byteArray], mediaFile.name, { 
              type: mediaFile.type 
            });
            
            formData.append('data', file);
            formData.append(`fileName_${index}`, mediaFile.name);
            formData.append(`fileType_${index}`, mediaFile.type);
            formData.append(`fileSize_${index}`, mediaFile.size.toString());
          } catch (fileError) {
            console.error(`Error processing Facebook video file ${index}:`, fileError);
            throw new Error(`Failed to process video file: ${mediaFile.name}`);
          }
        });
      }
      else if (payload.mediaFile && payload.mediaFile.data) {
        formData.append('mediaFileCount', '1');
        
        const byteCharacters = atob(payload.mediaFile.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const file = new File([byteArray], payload.mediaFile.name, { 
          type: payload.mediaFile.type 
        });
        
        formData.append('data', file);
        formData.append('fileName_0', payload.mediaFile.name);
        formData.append('fileType_0', payload.mediaFile.type);
        formData.append('fileSize_0', payload.mediaFile.size.toString());
      }

      const response = await fetch(webhookUrl, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Facebook Webhook API error:', response.status, errorText);
        throw new Error('Failed to send Facebook video post to social media.');
      }

      const result = await response.json();
      console.log('Facebook Webhook response:', result);

      const videoCount = payload.mediaFiles?.length || (payload.mediaFile ? 1 : 0);
      const successMessage = `Facebook video post with ${videoCount} file${videoCount !== 1 ? 's' : ''} sent successfully!`;

      return {
        success: true,
        data: result,
        message: successMessage,
        endpoint: webhookUrl
      };
    }
    else {
      // For Content Only, continue using JSON
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Facebook Webhook API error:', response.status, errorText);
        throw new Error('Failed to send Facebook post to social media.');
      }

      const result = await response.json();
      console.log('Facebook Webhook response:', result);

      return {
        success: true,
        data: result,
        message: 'Facebook post sent successfully!',
        endpoint: webhookUrl
      };
    }

  } catch (error) {
    console.error('Facebook API Network Error:', error);
    
    let errorMessage = 'Failed to connect to Facebook API';
    
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      errorMessage = 'Network error: Unable to connect to Facebook API. Please check your internet connection.';
    } else if (error.name === 'AbortError') {
      errorMessage = 'Facebook API request timeout. Please try again.';
    } else {
      errorMessage = error.message || errorMessage;
    }
    
    return {
      success: false,
      message: errorMessage,
      endpoint: 'https://n8n.insyts.co/webhook/facebook'
    };
  }
};

export const postToTwitter = async (payload) => {
  try {
    console.log('Posting to Twitter with payload:', payload);
    
    const response = await fetch('https://n8n.insyts.co/webhook/twitter', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    console.log('Twitter API Response Status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Twitter API Error Response:', errorText);
      
      let errorMessage = 'Failed to post to Twitter';
      
      // Try to parse error response
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (parseError) {
        // If parsing fails, use status-based message
        switch (response.status) {
          case 400:
            errorMessage = 'Invalid data sent to Twitter API. Please check your content and media files.';
            break;
          case 401:
            errorMessage = 'Twitter authentication failed. Please check your account credentials.';
            break;
          case 403:
            errorMessage = 'Twitter access denied. Please verify your account permissions.';
            break;
          case 429:
            errorMessage = 'Twitter API rate limit exceeded. Please try again later.';
            break;
          case 500:
            errorMessage = 'Twitter server error. Please try again later.';
            break;
          default:
            errorMessage = `Twitter API error (${response.status}). Please try again.`;
        }
      }
      
      return {
        success: false,
        message: errorMessage,
        status: response.status,
        endpoint: 'https://n8n.insyts.co/webhook/twitter'
      };
    }

    const responseData = await response.json();
    console.log('Twitter API Success Response:', responseData);
    
    return {
      success: true,
      message: responseData.message || 'Post successfully sent to Twitter!',
      data: responseData,
      endpoint: 'https://n8n.insyts.co/webhook/twitter'
    };
    
  } catch (error) {
    console.error('Twitter API Network Error:', error);
    
    let errorMessage = 'Failed to connect to Twitter API';
    
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      errorMessage = 'Network error: Unable to connect to Twitter API. Please check your internet connection.';
    } else if (error.name === 'AbortError') {
      errorMessage = 'Twitter API request timeout. Please try again.';
    } else {
      errorMessage = error.message || errorMessage;
    }
    
    return {
      success: false,
      message: errorMessage,
      endpoint: 'https://n8n.insyts.co/webhook/twitter'
    };
  }
};

// ADDED: LinkedIn-specific posting function for Images
export const postToLinkedIn = async (payload) => {
  try {
    console.log('Posting to LinkedIn with payload:', payload);

    // UPDATED: For Image content type, process each account individually with FormData (same as Instagram/Facebook)
    if (payload.contentType === 'Image') {
      const results = [];
      const errors = [];
      let successCount = 0;
      let failureCount = 0;

      // Loop through each account and make individual API calls
      for (let i = 0; i < payload.accounts.length; i++) {
        const account = payload.accounts[i];
        
        try {
          console.log(`Processing LinkedIn Image post for account ${i + 1}/${payload.accounts.length}:`, {
            accountId: account.accountId,
            accountName: account.accountName,
            provider: account.provider
          });

          // UPDATED: Use FormData for binary image data (same as Instagram/Facebook)
          const formData = new FormData();
          
          // ADDED: Determine image count and carousel setting
          let imageCount = 0;
          if (payload.mediaFiles && Array.isArray(payload.mediaFiles)) {
            imageCount = payload.mediaFiles.length;
          } else if (payload.mediaFile) {
            imageCount = 1;
          }
          
          console.log(`LinkedIn post has ${imageCount} image(s) for professional sharing`);
          
          // Add all non-file data as form fields
          formData.append('platforms', JSON.stringify(payload.platforms));
          formData.append('otherPlatform', payload.otherPlatform || '');
          formData.append('contentType', payload.contentType);
          formData.append('title', payload.title);
          formData.append('hashtags', payload.hashtags || '');
          formData.append('postContent', payload.postContent);
          formData.append('accounts', JSON.stringify([account])); // Pass only one account per API call
          formData.append('timestamp', payload.timestamp);
          formData.append('clientId', payload.clientId);
          formData.append('accountIndex', (i + 1).toString());
          formData.append('totalAccounts', payload.accounts.length.toString());

          // UPDATED: Handle multiple media files with consistent "data" field naming (same as Instagram/Facebook)
          if (payload.mediaFiles && Array.isArray(payload.mediaFiles) && payload.mediaFiles.length > 0) {
            formData.append('mediaFileCount', payload.mediaFiles.length.toString());
            
            payload.mediaFiles.forEach((mediaFile, index) => {
              try {
                const byteCharacters = atob(mediaFile.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let j = 0; j < byteCharacters.length; j++) {
                  byteNumbers[j] = byteCharacters.charCodeAt(j);
                }
                const byteArray = new Uint8Array(byteNumbers);
                
                const file = new File([byteArray], mediaFile.name, { 
                  type: mediaFile.type 
                });
                
                // Use "data" as the field name for all files (same as Instagram/Facebook)
                formData.append('data', file);
                formData.append(`fileName_${index}`, mediaFile.name);
                formData.append(`fileType_${index}`, mediaFile.type);
                formData.append(`fileSize_${index}`, mediaFile.size.toString());
                
                console.log(`Added file ${index + 1} as "data" for LinkedIn account ${account.accountId}:`, {
                  name: mediaFile.name,
                  type: mediaFile.type,
                  size: `${(mediaFile.size / 1024 / 1024).toFixed(2)} MB`
                });
              } catch (fileError) {
                console.error(`Error processing file ${index} for LinkedIn account ${account.accountId}:`, fileError);
                throw new Error(`Failed to process file: ${mediaFile.name}`);
              }
            });
          }
          // FALLBACK: Handle old single file structure
          else if (payload.mediaFile && payload.mediaFile.data) {
            formData.append('mediaFileCount', '1');
            
            const byteCharacters = atob(payload.mediaFile.data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let j = 0; j < byteCharacters.length; j++) {
              byteNumbers[j] = byteCharacters.charCodeAt(j);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const file = new File([byteArray], payload.mediaFile.name, { 
              type: payload.mediaFile.type 
            });
            
            formData.append('data', file);
            formData.append('fileName_0', payload.mediaFile.name);
            formData.append('fileType_0', payload.mediaFile.type);
            formData.append('fileSize_0', payload.mediaFile.size.toString());
            
            console.log(`Added single file as "data" for LinkedIn account ${account.accountId}:`, {
              name: payload.mediaFile.name,
              type: payload.mediaFile.type,
              size: `${(payload.mediaFile.size / 1024 / 1024).toFixed(2)} MB`
            });
          }

          console.log(`Sending FormData for LinkedIn account ${account.accountId} with`, formData.get('mediaFileCount'), 'files');

          // UPDATED: Send FormData instead of JSON for LinkedIn images
          const response = await fetch(`${LINKEDIN_API}`, {
            method: 'POST',
            body: formData // CHANGED: Send FormData instead of JSON
          });

          console.log('LinkedIn API Response Status:', response.status);

          // Handle response for this account
          if (!response.ok) {
            const errorText = await response.text();
            console.error(`LinkedIn API error for account ${account.accountId}:`, response.status, errorText);
            
            let errorMessage = `Failed to post image to LinkedIn ${account.accountName} (${account.provider})`;
            
            switch (response.status) {
              case 400:
                errorMessage += ': Invalid post data or media files';
                break;
              case 401:
                errorMessage += ': LinkedIn authentication failed';
                break;
              case 403:
                errorMessage += ': LinkedIn access denied or insufficient permissions';
                break;
              case 413:
                errorMessage += ': File size too large for LinkedIn (max 100MB)';
                break;
              case 415:
                errorMessage += ': Unsupported media format for LinkedIn';
                break;
              case 422:
                errorMessage += ': LinkedIn content validation failed';
                break;
              case 429:
                errorMessage += ': LinkedIn API rate limit exceeded';
                break;
              case 500:
                errorMessage += ': LinkedIn server error';
                break;
              case 503:
                errorMessage += ': LinkedIn service temporarily unavailable';
                break;
              default:
                errorMessage += `: LinkedIn API error (${response.status})`;
            }
            
            errors.push({
              account: account,
              error: errorMessage,
              status: response.status
            });
            failureCount++;
            
            // Continue to next account instead of throwing
            continue;
          }

          const result = await response.json();
          console.log(`LinkedIn Image post success for account ${account.accountId}:`, result);
          
          results.push({
            account: account,
            result: result,
            success: true
          });
          successCount++;

          // Add delay between requests to avoid rate limiting (LinkedIn has strict limits)
          if (i < payload.accounts.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay for LinkedIn
          }

        } catch (accountError) {
          console.error(`Error processing LinkedIn image post for account ${account.accountId}:`, accountError);
          
          errors.push({
            account: account,
            error: accountError.message || 'Unknown error occurred',
            status: 'network_error'
          });
          failureCount++;
        }
      }

      // Return comprehensive results for LinkedIn Image posts
      console.log(`LinkedIn Image API calls completed: ${successCount} successful, ${failureCount} failed`);

      // Create summary message
      let summaryMessage;
      if (successCount === payload.accounts.length) {
        const fileCount = payload.mediaFiles?.length || (payload.mediaFile ? 1 : 0);
        const postType = fileCount === 1 ? 'single image' : `post with ${fileCount} images`;
        summaryMessage = `LinkedIn ${postType} successfully shared to all ${successCount} account${successCount > 1 ? 's' : ''}!`;
      } else if (successCount > 0) {
        summaryMessage = `LinkedIn image post shared to ${successCount} of ${payload.accounts.length} accounts. ${failureCount} failed.`;
      } else {
        summaryMessage = `Failed to share LinkedIn image post to all accounts. Please check your account connections.`;
      }

      return {
        success: successCount > 0,
        data: {
          results: results,
          errors: errors,
          summary: {
            total: payload.accounts.length,
            successful: successCount,
            failed: failureCount,
            successRate: Math.round((successCount / payload.accounts.length) * 100)
          }
        },
        message: summaryMessage,
        endpoint: `${LINKEDIN_API}`,
        isPartialSuccess: successCount > 0 && failureCount > 0
      };
    }
    // UPDATED: For Video content type, also use FormData for binary data
    else if (payload.contentType === 'Video') {
      const results = [];
      const errors = [];
      let successCount = 0;
      let failureCount = 0;

      // Loop through each account and make individual API calls
      for (let i = 0; i < payload.accounts.length; i++) {
        const account = payload.accounts[i];
        
        try {
          console.log(`Processing LinkedIn Video post for account ${i + 1}/${payload.accounts.length}:`, {
            accountId: account.accountId,
            accountName: account.accountName,
            provider: account.provider,
            accessToken: account.accessToken ? 'Available' : 'Missing'
          });

          const formData = new FormData();
          
          // Add all non-file data as form fields
          formData.append('platforms', JSON.stringify(payload.platforms));
          formData.append('otherPlatform', payload.otherPlatform || '');
          formData.append('contentType', payload.contentType);
          formData.append('title', payload.title);
          formData.append('hashtags', payload.hashtags || '');
          formData.append('postContent', payload.postContent);
          formData.append('accounts', JSON.stringify([account])); // Pass only one account per API call
          formData.append('timestamp', payload.timestamp);
          formData.append('clientId', payload.clientId);
          formData.append('accountIndex', (i + 1).toString());
          formData.append('totalAccounts', payload.accounts.length.toString());
          
          // IMPORTANT: Add accessToken to FormData for LinkedIn video authentication
          if (account.accessToken) {
            formData.append('accessToken', account.accessToken);
          }

          // Handle multiple video files
          if (payload.mediaFiles && Array.isArray(payload.mediaFiles) && payload.mediaFiles.length > 0) {
            formData.append('mediaFileCount', payload.mediaFiles.length.toString());
            
            payload.mediaFiles.forEach((mediaFile, index) => {
              try {
                const byteCharacters = atob(mediaFile.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let j = 0; j < byteCharacters.length; j++) {
                  byteNumbers[j] = byteCharacters.charCodeAt(j);
                }
                const byteArray = new Uint8Array(byteNumbers);
                
                const file = new File([byteArray], mediaFile.name, { 
                  type: mediaFile.type 
                });
                
                formData.append('data', file);
                formData.append(`fileName_${index}`, mediaFile.name);
                formData.append(`fileType_${index}`, mediaFile.type);
                formData.append(`fileSize_${index}`, mediaFile.size.toString());
                
                console.log(`Added LinkedIn video file ${index + 1} for account ${account.accountId}:`, {
                  name: mediaFile.name,
                  type: mediaFile.type,
                  size: `${(mediaFile.size / 1024 / 1024).toFixed(2)} MB`
                });
              } catch (fileError) {
                console.error(`Error processing LinkedIn video file ${index}:`, fileError);
                throw new Error(`Failed to process video file: ${mediaFile.name}`);
              }
            });
          }
          // Handle single video file
          else if (payload.mediaFile && payload.mediaFile.data) {
            formData.append('mediaFileCount', '1');
            
            const byteCharacters = atob(payload.mediaFile.data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let j = 0; j < byteCharacters.length; j++) {
              byteNumbers[j] = byteCharacters.charCodeAt(j);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const file = new File([byteArray], payload.mediaFile.name, { 
              type: payload.mediaFile.type 
            });
            
            formData.append('data', file);
            formData.append('fileName_0', payload.mediaFile.name);
            formData.append('fileType_0', payload.mediaFile.type);
            formData.append('fileSize_0', payload.mediaFile.size.toString());
            
            console.log(`Added single LinkedIn video file for account ${account.accountId}:`, {
              name: payload.mediaFile.name,
              type: payload.mediaFile.type,
              size: `${(payload.mediaFile.size / 1024 / 1024).toFixed(2)} MB`
            });
          }

          console.log(`Sending video to LinkedIn Video API for account ${account.accountId}...`);

          // UPDATED: Use LINKEDIN_VIDEO_API endpoint for video uploads
          const response = await fetch(`${LINKEDIN_VIDEO_API}`, {
            method: 'POST',
            body: formData
          });

          console.log('LinkedIn Video API Response Status:', response.status);

          if (!response.ok) {
            const errorText = await response.text();
            console.error('LinkedIn Video API error for account', account.accountId, ':', response.status, errorText);
            
            let errorMessage = `Failed to post video to LinkedIn ${account.accountName} (${account.provider})`;
            
            switch (response.status) {
              case 400:
                errorMessage += ': Invalid video data or file format';
                break;
              case 401:
                errorMessage += ': LinkedIn authentication failed';
                break;
              case 403:
                errorMessage += ': LinkedIn access denied or insufficient permissions';
                break;
              case 413:
                errorMessage += ': Video file size too large (max 4MB for current setup)';
                break;
              case 415:
                errorMessage += ': Unsupported video format. Please use MP4 or MOV';
                break;
              case 422:
                errorMessage += ': LinkedIn video content validation failed';
                break;
              case 429:
                errorMessage += ': LinkedIn API rate limit exceeded';
                break;
              case 500:
                errorMessage += ': LinkedIn server error';
                break;
              default:
                errorMessage += `: LinkedIn API error (${response.status})`;
            }
            
            errors.push({
              account: account,
              error: errorMessage,
              status: response.status
            });
            failureCount++;
            
            // Continue to next account instead of throwing
            continue;
          }

          const result = await response.json();
          console.log(`LinkedIn Video post success for account ${account.accountId}:`, result);
          
          results.push({
            account: account,
            result: result,
            success: true
          });
          successCount++;

          // Add delay between requests to avoid rate limiting
          if (i < payload.accounts.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
          }

        } catch (accountError) {
          console.error(`Error processing LinkedIn video post for account ${account.accountId}:`, accountError);
          
          errors.push({
            account: account,
            error: accountError.message || 'Unknown error occurred',
            status: 'network_error'
          });
          failureCount++;
        }
      }

      // Return comprehensive results for LinkedIn Video posts
      console.log(`LinkedIn Video API calls completed: ${successCount} successful, ${failureCount} failed`);

      // Create summary message
      let summaryMessage;
      if (successCount === payload.accounts.length) {
        const videoCount = payload.mediaFiles?.length || (payload.mediaFile ? 1 : 0);
        summaryMessage = `LinkedIn video post successfully shared to all ${successCount} account${successCount > 1 ? 's' : ''}!`;
      } else if (successCount > 0) {
        summaryMessage = `LinkedIn video post shared to ${successCount} of ${payload.accounts.length} accounts. ${failureCount} failed.`;
      } else {
        summaryMessage = `Failed to share LinkedIn video post to all accounts. Please check your account connections and video file size (max 4MB).`;
      }

      return {
        success: successCount > 0,
        data: {
          results: results,
          errors: errors,
          summary: {
            total: payload.accounts.length,
            successful: successCount,
            failed: failureCount,
            successRate: Math.round((successCount / payload.accounts.length) * 100)
          }
        },
        message: summaryMessage,
        endpoint: `${LINKEDIN_VIDEO_API}`,
        isPartialSuccess: successCount > 0 && failureCount > 0
      };
    }
    // FALLBACK: For Content Only, use JSON (text posts)
    else {
      const response = await fetch(`${LINKEDIN_API}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log('LinkedIn Text API Response Status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('LinkedIn Text API Error Response:', errorText);
        
        let errorMessage = 'Failed to post to LinkedIn';
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          switch (response.status) {
            case 400:
              errorMessage = 'Invalid data sent to LinkedIn API. Please check your content.';
              break;
            case 401:
              errorMessage = 'LinkedIn authentication failed. Please check your account credentials.';
              break;
            case 403:
              errorMessage = 'LinkedIn access denied. Please verify your account permissions.';
              break;
            case 422:
              errorMessage = 'LinkedIn content validation failed. Please check your post content.';
              break;
            case 429:
              errorMessage = 'LinkedIn API rate limit exceeded. Please try again later.';
              break;
            case 500:
              errorMessage = 'LinkedIn server error. Please try again later.';
              break;
            default:
              errorMessage = `LinkedIn API error (${response.status}). Please try again.`;
          }
        }
        
        return {
          success: false,
          message: errorMessage,
          status: response.status,
          endpoint: `${LINKEDIN_API}`
        };
      }

      const responseData = await response.json();
      console.log('LinkedIn Text API Success Response:', responseData);
      
      return {
        success: true,
        message: responseData.message || 'Post successfully shared on LinkedIn!',
        data: responseData,
        endpoint: `${LINKEDIN_API}`
      };
    }
    
  } catch (error) {
    console.error('LinkedIn API Network Error:', error);
    
    let errorMessage = 'Failed to connect to LinkedIn API';
    
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      errorMessage = 'Network error: Unable to connect to LinkedIn API. Please check your internet connection.';
    } else if (error.name === 'AbortError') {
      errorMessage = 'LinkedIn API request timeout. Please try again.';
    } else if (error.message && error.message.includes('quota')) {
      errorMessage = 'LinkedIn API quota exceeded. Please try again later.';
    } else {
      errorMessage = error.message || errorMessage;
    }
    
    return {
      success: false,
      message: errorMessage,
      endpoint: `${LINKEDIN_API}`
    };
  }
};

export const postToInstagram = async (payload) => {
  try {
    console.log('Posting to Instagram with payload:', payload);

    // UPDATED: For Image content type, process each account individually with FormData (same as Facebook)
    if (payload.contentType === 'Image') {
      const results = [];
      const errors = [];
      let successCount = 0;
      let failureCount = 0;

      // Loop through each account and make individual API calls
      for (let i = 0; i < payload.accounts.length; i++) {
        const account = payload.accounts[i];
        
        try {
          console.log(`Processing Instagram Image post for account ${i + 1}/${payload.accounts.length}:`, {
            accountId: account.accountId,
            accountName: account.accountName,
            provider: account.provider
          });

          // UPDATED: Use FormData for binary image data (same as Facebook)
          const formData = new FormData();
          
          // ADDED: Determine carousel setting based on image count
          let imageCount = 0;
          if (payload.mediaFiles && Array.isArray(payload.mediaFiles)) {
            imageCount = payload.mediaFiles.length;
          } else if (payload.mediaFile) {
            imageCount = 1;
          }
          
          // ADDED: Set is_carousel_item based on image count
          const isCarouselItem = imageCount === 1 ? false : true;
          
          console.log(`Instagram post has ${imageCount} image(s), setting is_carousel_item to: ${isCarouselItem}`);
          
          // Add all non-file data as form fields
          formData.append('platforms', JSON.stringify(payload.platforms));
          formData.append('otherPlatform', payload.otherPlatform || '');
          formData.append('contentType', payload.contentType);
          formData.append('title', payload.title);
          formData.append('hashtags', payload.hashtags || '');
          formData.append('postContent', payload.postContent);
          formData.append('accounts', JSON.stringify([account])); // Pass only one account per API call
          formData.append('timestamp', payload.timestamp);
          formData.append('clientId', payload.clientId);
          formData.append('accountIndex', (i + 1).toString());
          formData.append('totalAccounts', payload.accounts.length.toString());
          formData.append('is_carousel_item', isCarouselItem.toString()); // ADDED: Carousel parameter

          // UPDATED: Handle multiple media files with consistent "data" field naming (same as Facebook)
          if (payload.mediaFiles && Array.isArray(payload.mediaFiles) && payload.mediaFiles.length > 0) {
            formData.append('mediaFileCount', payload.mediaFiles.length.toString());
            
            payload.mediaFiles.forEach((mediaFile, index) => {
              try {
                const byteCharacters = atob(mediaFile.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let j = 0; j < byteCharacters.length; j++) {
                  byteNumbers[j] = byteCharacters.charCodeAt(j);
                }
                const byteArray = new Uint8Array(byteNumbers);
                
                const file = new File([byteArray], mediaFile.name, { 
                  type: mediaFile.type 
                });
                
                // Use "data" as the field name for all files (same as Facebook)
                formData.append('data', file);
                formData.append(`fileName_${index}`, mediaFile.name);
                formData.append(`fileType_${index}`, mediaFile.type);
                formData.append(`fileSize_${index}`, mediaFile.size.toString());
                
                console.log(`Added file ${index + 1} as "data" for Instagram account ${account.accountId}:`, {
                  name: mediaFile.name,
                  type: mediaFile.type,
                  size: `${(mediaFile.size / 1024 / 1024).toFixed(2)} MB`
                });
              } catch (fileError) {
                console.error(`Error processing file ${index} for Instagram account ${account.accountId}:`, fileError);
                throw new Error(`Failed to process file: ${mediaFile.name}`);
              }
            });
          }
          // FALLBACK: Handle old single file structure
          else if (payload.mediaFile && payload.mediaFile.data) {
            formData.append('mediaFileCount', '1');
            
            const byteCharacters = atob(payload.mediaFile.data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let j = 0; j < byteCharacters.length; j++) {
              byteNumbers[j] = byteCharacters.charCodeAt(j);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const file = new File([byteArray], payload.mediaFile.name, { 
              type: payload.mediaFile.type 
            });
            
            formData.append('data', file);
            formData.append('fileName_0', payload.mediaFile.name);
            formData.append('fileType_0', payload.mediaFile.type);
            formData.append('fileSize_0', payload.mediaFile.size.toString());
            
            console.log(`Added single file as "data" for Instagram account ${account.accountId}:`, {
              name: payload.mediaFile.name,
              type: payload.mediaFile.type,
              size: `${(payload.mediaFile.size / 1024 / 1024).toFixed(2)} MB`
            });
          }

          console.log(`Sending FormData for Instagram account ${account.accountId} with`, formData.get('mediaFileCount'), 'files and is_carousel_item:', formData.get('is_carousel_item'));

          // UPDATED: Send FormData instead of JSON for Instagram images
          const response = await fetch(`${INSTAGRAM_API}`, {
            method: 'POST',
            body: formData // CHANGED: Send FormData instead of JSON
          });

          console.log('Instagram API Response Status:', response.status);

          // Handle response for this account
          if (!response.ok) {
            const errorText = await response.text();
            console.error(`Instagram API error for account ${account.accountId}:`, response.status, errorText);
            
            let errorMessage = `Failed to post image to Instagram ${account.accountName} (${account.provider})`;
            
            switch (response.status) {
              case 400:
                errorMessage += ': Invalid post data or media files';
                break;
              case 401:
                errorMessage += ': Instagram authentication failed';
                break;
              case 403:
                errorMessage += ': Instagram access denied';
                break;
              case 413:
                errorMessage += ': File size too large for Instagram';
                break;
              case 415:
                errorMessage += ': Unsupported media format for Instagram';
                break;
              case 429:
                errorMessage += ': Instagram API rate limit exceeded';
                break;
              case 500:
                errorMessage += ': Instagram server error';
                break;
              default:
                errorMessage += `: Instagram API error (${response.status})`;
            }
            
            errors.push({
              account: account,
              error: errorMessage,
              status: response.status
            });
            failureCount++;
            
            // Continue to next account instead of throwing
            continue;
          }

          const result = await response.json();
          console.log(`Instagram Image post success for account ${account.accountId}:`, result);
          
          results.push({
            account: account,
            result: result,
            success: true
          });
          successCount++;

          // Add delay between requests to avoid rate limiting
          if (i < payload.accounts.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
          }

        } catch (accountError) {
          console.error(`Error processing Instagram image post for account ${account.accountId}:`, accountError);
          
          errors.push({
            account: account,
            error: accountError.message || 'Unknown error occurred',
            status: 'network_error'
          });
          failureCount++;
        }
      }

      // Return comprehensive results for Instagram Image posts
      console.log(`Instagram Image API calls completed: ${successCount} successful, ${failureCount} failed`);

      // Create summary message
      let summaryMessage;
      if (successCount === payload.accounts.length) {
        const fileCount = payload.mediaFiles?.length || (payload.mediaFile ? 1 : 0);
        const postType = fileCount === 1 ? 'single image' : `carousel with ${fileCount} images`;
        summaryMessage = `Instagram ${postType} post successfully sent to all ${successCount} account${successCount > 1 ? 's' : ''}!`;
      } else if (successCount > 0) {
        summaryMessage = `Instagram image post sent to ${successCount} of ${payload.accounts.length} accounts. ${failureCount} failed.`;
      } else {
        summaryMessage = `Failed to send Instagram image post to all accounts. Please check your account connections.`;
      }

      return {
        success: successCount > 0,
        data: {
          results: results,
          errors: errors,
          summary: {
            total: payload.accounts.length,
            successful: successCount,
            failed: failureCount,
            successRate: Math.round((successCount / payload.accounts.length) * 100)
          }
        },
        message: summaryMessage,
        endpoint: `${INSTAGRAM_API}`,
        isPartialSuccess: successCount > 0 && failureCount > 0
      };
    }
    // FALLBACK: For non-Image content, use JSON (though Instagram is primarily for images)
    else {
      const response = await fetch(`${INSTAGRAM_API}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...payload,
          is_carousel_item: true // ADDED: Default to true for non-image content
        })
      });

      console.log('Instagram API Response Status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Instagram API Error Response:', errorText);
        
        let errorMessage = 'Failed to post to Instagram';
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          switch (response.status) {
            case 400:
              errorMessage = 'Invalid data sent to Instagram API. Please check your content.';
              break;
            case 401:
              errorMessage = 'Instagram authentication failed. Please check your account credentials.';
              break;
            case 403:
              errorMessage = 'Instagram access denied. Please verify your account permissions.';
              break;
            case 429:
              errorMessage = 'Instagram API rate limit exceeded. Please try again later.';
              break;
            case 500:
              errorMessage = 'Instagram server error. Please try again later.';
              break;
            default:
              errorMessage = `Instagram API error (${response.status}). Please try again.`;
          }
        }
        
        return {
          success: false,
          message: errorMessage,
          status: response.status,
          endpoint: `${INSTAGRAM_API}`
        };
      }

      const responseData = await response.json();
      console.log('Instagram API Success Response:', responseData);
      
      return {
        success: true,
        message: responseData.message || 'Post successfully sent to Instagram!',
        data: responseData,
        endpoint: `${INSTAGRAM_API}`
      };
    }
    
  } catch (error) {
    console.error('Instagram API Network Error:', error);
    
    let errorMessage = 'Failed to connect to Instagram API';
    
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      errorMessage = 'Network error: Unable to connect to Instagram API. Please check your internet connection.';
    } else if (error.name === 'AbortError') {
      errorMessage = 'Instagram API request timeout. Please try again.';
    } else {
      errorMessage = error.message || errorMessage;
    }
    
    return {
      success: false,
      message: errorMessage,
      endpoint: `${INSTAGRAM_API}`
    };
  }
};

// ADDED: YouTube-specific posting function
export const postToYoutube = async (payload) => {
  try {
    console.log('Posting to YouTube with payload:', payload);
    
    const response = await fetch('https://n8n.insyts.co/webhook/youtube', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    console.log('YouTube API Response Status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('YouTube API Error Response:', errorText);
      
      let errorMessage = 'Failed to post to YouTube';
      
      // Try to parse error response
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (parseError) {
        // If parsing fails, use status-based message
        switch (response.status) {
          case 400:
            errorMessage = 'Invalid data sent to YouTube API. Please check your content and media files.';
            break;
          case 401:
            errorMessage = 'YouTube authentication failed. Please check your account credentials.';
            break;
          case 403:
            errorMessage = 'YouTube access denied. Please verify your account permissions.';
            break;
          case 413:
            errorMessage = 'File size too large for YouTube. Please reduce video file size.';
            break;
          case 415:
            errorMessage = 'Unsupported media format for YouTube. Please use supported video formats.';
            break;
          case 429:
            errorMessage = 'YouTube API rate limit exceeded. Please try again later.';
            break;
          case 500:
            errorMessage = 'YouTube server error. Please try again later.';
            break;
          case 503:
            errorMessage = 'YouTube service temporarily unavailable. Please try again later.';
            break;
          default:
            errorMessage = `YouTube API error (${response.status}). Please try again.`;
        }
      }
      
      return {
        success: false,
        message: errorMessage,
        status: response.status,
        endpoint: 'https://n8n.insyts.co/webhook/youtube'
      };
    }

    const responseData = await response.json();
    console.log('YouTube API Success Response:', responseData);
    
    // Create success message based on content type
    let successMessage = responseData.message || 'Post successfully sent to YouTube!';
    
    if (payload.contentType === 'Video') {
      const videoCount = payload.mediaFiles?.length || (payload.mediaFile ? 1 : 0);
      successMessage = `YouTube video${videoCount > 1 ? 's' : ''} (${videoCount} file${videoCount > 1 ? 's' : ''}) uploaded successfully!`;
    } else if (payload.contentType === 'Image') {
      const imageCount = payload.mediaFiles?.length || (payload.mediaFile ? 1 : 0);
      successMessage = `YouTube post with ${imageCount} image${imageCount > 1 ? 's' : ''} uploaded successfully!`;
    } else if (payload.contentType === 'Content Only') {
      successMessage = 'YouTube text post published successfully!';
    }
    
    return {
      success: true,
      message: successMessage,
      data: responseData,
      endpoint: 'https://n8n.insyts.co/webhook/youtube'
    };
    
  } catch (error) {
    console.error('YouTube API Network Error:', error);
    
    let errorMessage = 'Failed to connect to YouTube API';
    
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      errorMessage = 'Network error: Unable to connect to YouTube API. Please check your internet connection.';
    } else if (error.name === 'AbortError') {
      errorMessage = 'YouTube API request timeout. Please try again.';
    } else if (error.message && error.message.includes('quota')) {
      errorMessage = 'YouTube API quota exceeded. Please try again later.';
    } else {
      errorMessage = error.message || errorMessage;
    }
    
    return {
      success: false,
      message: errorMessage,
      endpoint: 'https://n8n.insyts.co/webhook/youtube'
    };
  }
};

/**
 * Fetch Facebook Analytics Data for a specific page
 * @param {string} pageId - The Facebook page ID
 * @param {string} accessToken - The Facebook access token
 * @returns {Promise<Object>} API response with Facebook analytics data
 */
export const fetchFacebookAnalytics = async (pageId, accessToken) => {
  console.log('fetchFacebookAnalytics called with:', { pageId, accessToken: accessToken ? '***' : 'missing' });
  
  try {
    const requestBody = {
      pageId: pageId,
      accessToken: accessToken,
    };
    
    console.log('Making POST request to https://n8n.insyts.co/webhook/facebook');
    
    const response = await fetch('https://n8n.insyts.co/webhook/facebook', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    console.log('Response status:', response.status);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Response data:', result);

    // Check if result is an array or contains data array
    const analyticsData = Array.isArray(result) ? result : (result.data || []);

    return {
      success: true,
      data: analyticsData,
      message: "Facebook analytics loaded successfully",
    };
  } catch (error) {
    console.error("Error in fetchFacebookAnalytics:", error);
    return {
      success: false,
      data: [],
      message: error.message || "Network error occurred while fetching Facebook analytics",
    };
  }
};

/**
 * Fetch Instagram analytics data
 * @param {string} pageId - The Instagram Business Account ID
 * @param {string} accessToken - The Instagram access token
 * @returns {Promise<Object>} API response with Instagram analytics data
 */
export const fetchInstagramAnalytics = async (pageId, accessToken) => {
  console.log('fetchInstagramAnalytics called with:', { pageId, accessToken: accessToken ? '***' : 'missing' });
  
  try {
    const requestBody = {
      pageId: pageId,
      accessToken: accessToken,
    };
    
    console.log('Making POST request to https://n8n.insyts.co/webhook/instagram-dashboard-view');
    
    const response = await fetch('https://n8n.insyts.co/webhook/instagram-dashboard-view', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    console.log('Response status:', response.status);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Response data:', result);

    // Result should be an array containing profile and media data
    const analyticsData = Array.isArray(result) ? result : (result.data || []);

    return {
      success: true,
      data: analyticsData,
      message: "Instagram analytics loaded successfully",
    };
  } catch (error) {
    console.error("Error in fetchInstagramAnalytics:", error);
    return {
      success: false,
      data: [],
      message: error.message || "Network error occurred while fetching Instagram analytics",
    };
  }
};

/**
 * Fetch LinkedIn analytics data
 * @param {string} pageId - The LinkedIn page ID/username
 * @param {string} accessToken - The LinkedIn access token
 * @returns {Promise<Object>} API response with LinkedIn analytics data
 */
export const fetchLinkedInAnalytics = async (pageId, accessToken) => {
  console.log('fetchLinkedInAnalytics called with:', { pageId, accessToken: accessToken ? '***' : 'missing' });
  
  try {
    const requestBody = {
      pageId: pageId,
      accessToken: accessToken,
    };
    
    console.log('Making POST request to https://n8n.insyts.co/webhook/linkedin-dashboard-view');
    
    const response = await fetch('https://n8n.insyts.co/webhook/linkedin-dashboard-view', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    console.log('Response status:', response.status);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Response data:', result);

    // Result is an array containing paging info and elements
    const analyticsData = Array.isArray(result) ? result : (result.data || []);

    return {
      success: true,
      data: analyticsData,
      message: "LinkedIn analytics loaded successfully",
    };
  } catch (error) {
    console.error("Error in fetchLinkedInAnalytics:", error);
    return {
      success: false,
      data: [],
      message: error.message || "Network error occurred while fetching LinkedIn analytics",
    };
  }
};

/**
 * Fetch LinkedIn share statistics (engagement metrics)
 * @param {string} pageId - The LinkedIn page ID/username
 * @param {string} accessToken - The LinkedIn access token
 * @returns {Promise<Object>} API response with LinkedIn share statistics
 */
export const fetchLinkedInShareStatistics = async (pageId, accessToken) => {
  console.log('fetchLinkedInShareStatistics called with:', { pageId, accessToken: accessToken ? '***' : 'missing' });
  
  try {
    const requestBody = {
      pageId: pageId,
      accessToken: accessToken,
    };
    
    console.log('Making POST request to https://n8n.insyts.co/webhook/share-statistics');
    
    const response = await fetch('https://n8n.insyts.co/webhook/share-statistics', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    console.log('Share statistics response status:', response.status);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Share statistics response data:', result);

    return {
      success: true,
      data: result,
      message: "LinkedIn share statistics loaded successfully",
    };
  } catch (error) {
    console.error("Error in fetchLinkedInShareStatistics:", error);
    return {
      success: false,
      data: null,
      message: error.message || "Network error occurred while fetching LinkedIn share statistics",
    };
  }
};